import { useEffect } from 'react'

interface StructuredDataProps {
  type?: 'website' | 'organization' | 'service' | 'webpage' | 'newspost'
}

function StructuredData({ type = 'website' }: StructuredDataProps) {
  useEffect(() => {
    const siteUrl = 'https://claverton42.com'
    
    const baseData = {
      '@context': 'https://schema.org',
    }

    let structuredData: Record<string, unknown> = baseData

    switch (type) {
      case 'website':
        structuredData = {
          ...baseData,
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: 'Claverton 42',
          description: 'Fractional CTO services for growing companies',
          inLanguage: 'en-GB',
        }
        break
      case 'organization':
        structuredData = {
          ...baseData,
          '@type': 'ProfessionalService',
          '@id': `${siteUrl}/#organization`,
          name: 'Claverton 42',
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: `${siteUrl}/logo.png`,
          },
          image: {
            '@type': 'ImageObject',
            url: `${siteUrl}/og-image.png`,
            width: 1200,
            height: 630,
          },
          email: 'mailto:hello@claverton42.com',
          areaServed: {
            '@type': 'Country',
            name: 'United Kingdom',
          },
          serviceType: 'Fractional CTO Services',
          description: 'Executive-level technology leadership, security guidance, and strategic planning for growing companies - engaged precisely when you need it.',
        }
        break
      case 'service':
        structuredData = {
          ...baseData,
          '@type': 'Service',
          '@id': `${siteUrl}/#service`,
          serviceType: 'Fractional CTO',
          provider: {
            '@id': `${siteUrl}/#organization`,
          },
          areaServed: {
            '@type': 'Country',
            name: 'United Kingdom',
          },
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'CTO Services',
            itemListElement: [
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Technology Roadmap Development' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Architecture Review' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vendor Evaluation' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Team Building & Hiring' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security & Compliance' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Digital Transformation' } },
            ],
          },
        }
        break
      case 'webpage':
        structuredData = {
          ...baseData,
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: siteUrl,
          name: 'Claverton 42 - Fractional CTO Services in the UK',
          isPartOf: {
            '@id': `${siteUrl}/#website`,
          },
          about: {
            '@id': `${siteUrl}/#organization`,
          },
          primaryImageOfPage: {
            '@id': `${siteUrl}/og-image.png`,
          },
        }
        break
    }

    // Find or create the script element
    let script = document.getElementById('structured-data') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'structured-data'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    
    // For homepage, include the full graph
    if (type === 'website') {
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebSite',
            '@id': `${siteUrl}/#website`,
            url: siteUrl,
            name: 'Claverton 42',
            description: 'Fractional CTO services for growing companies',
            inLanguage: 'en-GB',
          },
          {
            '@type': 'ProfessionalService',
            '@id': `${siteUrl}/#organization`,
            name: 'Claverton 42',
            url: siteUrl,
            logo: {
              '@type': 'ImageObject',
              url: `${siteUrl}/logo.png`,
            },
            image: {
              '@type': 'ImageObject',
              url: `${siteUrl}/og-image.png`,
              width: 1200,
              height: 630,
            },
            email: 'mailto:hello@claverton42.com',
            areaServed: {
              '@type': 'Country',
              name: 'United Kingdom',
            },
            serviceType: 'Fractional CTO Services',
            description: 'Executive-level technology leadership, security guidance, and strategic planning for growing companies - engaged precisely when you need it.',
          },
          {
            '@type': 'Service',
            '@id': `${siteUrl}/#service`,
            serviceType: 'Fractional CTO',
            provider: {
              '@id': `${siteUrl}/#organization`,
            },
            areaServed: {
              '@type': 'Country',
              name: 'United Kingdom',
            },
            hasOfferCatalog: {
              '@type': 'OfferCatalog',
              name: 'CTO Services',
              itemListElement: [
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Technology Roadmap Development' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Architecture Review' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Vendor Evaluation' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Team Building & Hiring' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Security & Compliance' } },
                { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Digital Transformation' } },
              ],
            },
          },
        ],
      })
    } else {
      script.textContent = JSON.stringify(structuredData)
    }

    return () => {
      const scriptToRemove = document.getElementById('structured-data')
      if (scriptToRemove) {
        document.head.removeChild(scriptToRemove)
      }
    }
  }, [type])

  return null
}

export default StructuredData
